'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketParams } from '@morpho-org/blue-sdk';
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useStickyMaxInput } from '@/hooks/useStickyMaxInput';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { isMarketLendFlowEnabled } from '@/lib/shared/utils/flags.constants';
import useAssetCanTransferOnchain from '../../useAssetCanTransferOnchain';
import { PREVIEW_DEBOUNCE_MS } from './marketAction.helpers';
import { getMarketActionError } from './marketActionError';
import { useMarketActionBuilder } from './useMarketActionBuilder';
import { useMarketBlue } from './useMarketBlue';
import { useMarketLendNativeWrap } from './useMarketLendNativeWrap';
export interface UseMarketLendFormParams {
  marketParams: MarketParams | undefined;
  loanAsset: InterfaceToken | null | undefined;
}

/** Form model behind the market-page direct-lend panel. */
export function useMarketLendForm(t0) {
  const $ = _c(72);
  const {
    marketParams,
    loanAsset
  } = t0;
  const {
    account,
    targetChain
  } = useWeb3Context();
  const flags = useFeatureFlags();
  const [inputLendAmount, setInputLendAmount] = useState(ZERO);
  let t1;
  if ($[0] !== flags || $[1] !== marketParams) {
    t1 = isMarketLendFlowEnabled(flags) && !!marketParams;
    $[0] = flags;
    $[1] = marketParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isMarketLendEnabled = t1;
  const [debouncedLend] = useDebounce(inputLendAmount, PREVIEW_DEBOUNCE_MS);
  const isDebouncing = debouncedLend !== inputLendAmount;
  const {
    positionData,
    error: positionError,
    isLoading: isPositionLoading
  } = useMarketBlue(marketParams);
  const currentSupply = positionData?.supplyAssets;
  const isBlockedByBorrowPosition = !!positionData && (positionData.borrowShares > ZERO || positionData.collateral > ZERO);
  const t2 = loanAsset?.address;
  let t3;
  if ($[3] !== inputLendAmount || $[4] !== isMarketLendEnabled || $[5] !== t2) {
    t3 = {
      loanToken: t2,
      totalLend: inputLendAmount,
      enabled: isMarketLendEnabled
    };
    $[3] = inputLendAmount;
    $[4] = isMarketLendEnabled;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    isWNativeLoan,
    isWrapping,
    onWrappingChange,
    wrappableNativeBalance,
    nativeAmount,
    maxLend,
    isMaxLoading,
    isWrappingToggleLoading,
    wrapErrorMessage
  } = useMarketLendNativeWrap(t3);
  let t4;
  if ($[7] !== maxLend) {
    t4 = {
      maxValue: maxLend,
      onChange: setInputLendAmount
    };
    $[7] = maxLend;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    isMaxActive,
    onMaxClick,
    onManualChange: setInputLendAmountManually
  } = useStickyMaxInput(t4);
  let t5;
  if ($[9] !== inputLendAmount || $[10] !== isMarketLendEnabled || $[11] !== maxLend) {
    t5 = isMarketLendEnabled && isDefined(maxLend) && inputLendAmount > maxLend;
    $[9] = inputLendAmount;
    $[10] = isMarketLendEnabled;
    $[11] = maxLend;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const isLendOverMax = t5;
  let t6;
  if ($[13] !== debouncedLend || $[14] !== marketParams || $[15] !== nativeAmount) {
    t6 = {
      marketParams,
      mode: "lend",
      collateralAmount: ZERO,
      loanAmount: debouncedLend,
      nativeAmount,
      withRequirements: false
    };
    $[13] = debouncedLend;
    $[14] = marketParams;
    $[15] = nativeAmount;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const {
    buildError
  } = useMarketActionBuilder(t6);
  const isEmpty = inputLendAmount === ZERO;
  let t7;
  if ($[17] !== currentSupply || $[18] !== inputLendAmount || $[19] !== isEmpty) {
    t7 = isEmpty || !isDefined(currentSupply) ? undefined : currentSupply + inputLendAmount;
    $[17] = currentSupply;
    $[18] = inputLendAmount;
    $[19] = isEmpty;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const afterSupply = t7;
  let t8;
  if ($[21] !== buildError || $[22] !== isDebouncing || $[23] !== isEmpty || $[24] !== isLendOverMax || $[25] !== loanAsset?.symbol || $[26] !== wrapErrorMessage) {
    t8 = () => {
      if (wrapErrorMessage) {
        return wrapErrorMessage;
      }
      if (isLendOverMax) {
        return getMarketActionError({
          buildError: undefined,
          insufficientBalanceSymbol: loanAsset?.symbol
        });
      }
      if (isEmpty || isDebouncing) {
        return;
      }
      return getMarketActionError({
        buildError
      });
    };
    $[21] = buildError;
    $[22] = isDebouncing;
    $[23] = isEmpty;
    $[24] = isLendOverMax;
    $[25] = loanAsset?.symbol;
    $[26] = wrapErrorMessage;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  loanAsset?.symbol;
  let t9;
  if ($[28] !== t8) {
    t9 = t8();
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const buildErrorMessage = t9;
  const canTransferLoanAsset = useAssetCanTransferOnchain(account.address, loanAsset?.address, targetChain.id);
  let t10;
  if ($[30] !== account.isWrongChain || $[31] !== buildErrorMessage || $[32] !== canTransferLoanAsset || $[33] !== isBlockedByBorrowPosition || $[34] !== isEmpty || $[35] !== isLendOverMax || $[36] !== isMarketLendEnabled || $[37] !== isMaxLoading || $[38] !== isPositionLoading || $[39] !== maxLend || $[40] !== positionError) {
    t10 = !isMarketLendEnabled || isEmpty || isPositionLoading || !!positionError || isBlockedByBorrowPosition || isMaxLoading || !isDefined(maxLend) || account.isWrongChain || !!buildErrorMessage || isLendOverMax || !canTransferLoanAsset;
    $[30] = account.isWrongChain;
    $[31] = buildErrorMessage;
    $[32] = canTransferLoanAsset;
    $[33] = isBlockedByBorrowPosition;
    $[34] = isEmpty;
    $[35] = isLendOverMax;
    $[36] = isMarketLendEnabled;
    $[37] = isMaxLoading;
    $[38] = isPositionLoading;
    $[39] = maxLend;
    $[40] = positionError;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const isFinalizeDisabled = t10;
  let t11;
  if ($[42] !== onMaxClick || $[43] !== onWrappingChange) {
    t11 = () => {
      setInputLendAmount(ZERO);
      onMaxClick(false);
      onWrappingChange(false);
    };
    $[42] = onMaxClick;
    $[43] = onWrappingChange;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  const reset = t11;
  const t12 = account.isConnected && !positionError && isPositionLoading;
  let t13;
  if ($[45] !== afterSupply || $[46] !== buildErrorMessage || $[47] !== canTransferLoanAsset || $[48] !== currentSupply || $[49] !== inputLendAmount || $[50] !== isBlockedByBorrowPosition || $[51] !== isDebouncing || $[52] !== isEmpty || $[53] !== isFinalizeDisabled || $[54] !== isMarketLendEnabled || $[55] !== isMaxActive || $[56] !== isMaxLoading || $[57] !== isPositionLoading || $[58] !== isWNativeLoan || $[59] !== isWrapping || $[60] !== isWrappingToggleLoading || $[61] !== maxLend || $[62] !== nativeAmount || $[63] !== onMaxClick || $[64] !== onWrappingChange || $[65] !== positionData || $[66] !== positionError || $[67] !== reset || $[68] !== setInputLendAmountManually || $[69] !== t12 || $[70] !== wrappableNativeBalance) {
    t13 = {
      isMarketLendEnabled,
      inputLendAmount,
      setInputLendAmountManually,
      isEmpty,
      positionData,
      positionError,
      isPositionLoading,
      currentSupply,
      afterSupply,
      maxLend,
      isMaxActive,
      isMaxLoading,
      onMaxClick,
      isWNativeLoan,
      isWrapping,
      onWrappingChange,
      wrappableNativeBalance,
      nativeAmount,
      isWrappingToggleLoading,
      buildErrorMessage,
      canTransferLoanAsset,
      isBlockedByBorrowPosition,
      isDebouncing,
      isFinalizeDisabled,
      isButtonLoading: t12,
      reset
    };
    $[45] = afterSupply;
    $[46] = buildErrorMessage;
    $[47] = canTransferLoanAsset;
    $[48] = currentSupply;
    $[49] = inputLendAmount;
    $[50] = isBlockedByBorrowPosition;
    $[51] = isDebouncing;
    $[52] = isEmpty;
    $[53] = isFinalizeDisabled;
    $[54] = isMarketLendEnabled;
    $[55] = isMaxActive;
    $[56] = isMaxLoading;
    $[57] = isPositionLoading;
    $[58] = isWNativeLoan;
    $[59] = isWrapping;
    $[60] = isWrappingToggleLoading;
    $[61] = maxLend;
    $[62] = nativeAmount;
    $[63] = onMaxClick;
    $[64] = onWrappingChange;
    $[65] = positionData;
    $[66] = positionError;
    $[67] = reset;
    $[68] = setInputLendAmountManually;
    $[69] = t12;
    $[70] = wrappableNativeBalance;
    $[71] = t13;
  } else {
    t13 = $[71];
  }
  return t13;
}